import { defineStore } from "pinia"
import { computed, reactive, ref, watch } from "vue"
import { getDefaultState, type IntProviderState } from "./IntProviderState"
import backendService from "@/common/services/BackendService"


export const useProviderStore = defineStore("providerStore", () => {
  const state = reactive<IntProviderState>(getDefaultState())

  const selectedUsecase = reactive<any>({})
  const modelsForSelectedUsecase = ref<any>([])

  const showWizardGettingStartedPopup = ref<boolean>(false)

  const useCases = computed(() => state.useCases)

  const models = computed(() => state.models)

  const assetProvisionForm = computed(() => {
    return state.assetProvisionForm
  })

  const digitalTwinTypes = computed(() => {
    return state.digitalTwinTypes
  })

  const offerCategories = computed(() => {
    return state.offerCategories
  })

  // it watches changes in selected Usecase (provider - Step1)
  // accordingly filters the models list (modelsForSelectedUsecase) for the same component
  watch(selectedUsecase, (newUsecase: any, _: any) => {
    const shellTypes = newUsecase.value.shellTypes
    const shellTypesNames = Object.keys(shellTypes)
    const modelsIds = shellTypesNames
      .map((name: any) => Object.keys(shellTypes[name].conversions))
      .flat()

    modelsForSelectedUsecase.value = models.value.data.filter((model: any) =>
      modelsIds.includes(model._id)
    )
  })

  const setSelectedUsecase = (data: any) => {
    selectedUsecase.value = data
  }

  const getModelsForSelectedUsecase = computed(() => {
    return modelsForSelectedUsecase.value
  })

  const isShowWizardGettingStartedPopup = computed(() => {
    return showWizardGettingStartedPopup.value
  })

  function updateIsShowWizardGettingStartedPopup(value: boolean) {
    showWizardGettingStartedPopup.value = value || !showWizardGettingStartedPopup.value
  }

  const updateFormValues = (key: string, value: string | number) => {
    state.assetProvisionForm[key] = value
  }

  const aspectsCountData = computed(() => state.aspectsCountData)

  const connectorCountData = computed(() => state.connectorCountData)

  const setInitiateState = (payload: {
    key: keyof IntProviderState
    data: any
    selectionType: "single" | "multiselect"
  }): void => {
    if (payload.key === "offerCategories") {
      // reset other selection if create offer category changes
      state.useCases.selectedData = []
      state.digitalTwinTypes.selectedData = []
    }

    if(payload.key === "offerCategories" || payload.key === "useCases") {
      state.digitalTwinTypes.data = []
      state.digitalTwinTypes.selectedData = []
      state.models.data = []
      state.models.selectedData = []
    }

    state[payload.key].selectedData = []
    if (Array.isArray(payload.data)) {
      state[payload.key].selectedData = payload.data
    } else {
      state[payload.key]?.selectedData?.push(payload.data)
    }

    // populate digital twin types as per usecases
    if (payload.key === "useCases") {
      const _digitalTwinTypes: unknown[] = []
      state.useCases.selectedData?.forEach((item) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        Object.entries(item.shellTypes).forEach((attr) => {
          const [key, value] = attr
          _digitalTwinTypes.push({ ...(value as {}), _id: key })
        })
      })
      state.digitalTwinTypes.data = _digitalTwinTypes

      const _subModels: any[] = []
      state.digitalTwinTypes.data?.forEach((item) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        Object.entries(item.conversions).forEach((attr) => {
          const [key, value] = attr
          _subModels.push({ ...(value as {}), _id: key, shellName: item.name })
        })
      })
      const _subModelsFiltered = [..._subModels]
      state.models.data = _subModelsFiltered
    }

    // populate sub models from digital twin types
    if (payload.key === "digitalTwinTypes") {
      const _subModels: any[] = []
      state.digitalTwinTypes.selectedData?.forEach((item) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        Object.entries(item.conversions).forEach((attr) => {
          const [key, value] = attr
          _subModels.push({ ...(value as {}), _id: key, shellName: item.name })
        })
      })
      const _subModelsFiltered = [..._subModels]
      state.models.data = _subModelsFiltered
    }
  }

  const fetchUsecases = async () => {
    try {
      state.useCases.isLoading = true
      const { data } = await backendService.fetchUseCases()
      state.useCases.data = data.content

      // populate submodels
      const _allModels: any[] = []
      state.useCases.data.forEach((item) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        Object.keys(item.shellTypes).forEach((skey) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          Object.entries(item.shellTypes[skey].conversions).forEach((attr) => {
            const [key, value] = attr
            _allModels.push({ ...(value as {}), _id: key, shellName: item.shellTypes[skey].name })
          })
        })
      })
      const _subModelsFiltered = [..._allModels]
      state.models.data = _subModelsFiltered
    } catch {
      state.useCases.data = []
      state.models.data = []
    } finally {
      state.useCases.isLoading = false
    }
  }

  const fetchSubmodels = async () => {
    try {
      const res = await backendService.fetchModels()
    } catch {}
  }

  const fetchAspectsCount = async () => {
    try {
      const res = await backendService.fetchAspectsCount()
      state.aspectsCountData = res.data?.count
    } catch {
      state.aspectsCountData = 0
    }
  }

  const fetchConectorsCount = async () => {
    try {
      const res = await backendService.fetchConectorsCount()
      state.connectorCountData = res.data[0]?.connectorEndpoint?.length
    } catch {
      state.connectorCountData = 0
    }
  }

  return {
    useCases,
    models,
    digitalTwinTypes,
    setInitiateState,
    offerCategories,
    isShowWizardGettingStartedPopup,
    updateIsShowWizardGettingStartedPopup,
    assetProvisionForm,
    updateFormValues,
    fetchUsecases,
    fetchSubmodels,
    setSelectedUsecase,
    getModelsForSelectedUsecase,
    fetchAspectsCount,
    fetchConectorsCount,
    aspectsCountData,
    connectorCountData
  }
})
